import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveIcon from '@mui/icons-material/Archive';
import EditIcon from '@mui/icons-material/Edit';
import { SDialog, StyledTextField } from '@modules/ui-components';
import { useLocaleAuthoring } from '../hooks/useLocaleAuthoring';
import { LOCALE_CONFIG } from '../selectors';
import type {
  ProjectLocaleGlossary,
  ProjectLocaleProfile,
  ProjectLocaleProfileContext,
} from '../types/localeConfig.types';

interface LocaleAuthoringSectionProps {
  projectId: string;
}

type ProfileDialogState = { mode: 'create'; resource?: undefined } | { mode: 'edit'; resource: ProjectLocaleProfile };
type GlossaryDialogState = { mode: 'create'; resource?: undefined } | { mode: 'edit'; resource: ProjectLocaleGlossary };

export const LocaleAuthoringSection: React.FC<LocaleAuthoringSectionProps> = ({ projectId }) => {
  const authoring = useLocaleAuthoring(projectId);
  const [profileDialog, setProfileDialog] = useState<ProfileDialogState | null>(null);
  const [glossaryDialog, setGlossaryDialog] = useState<GlossaryDialogState | null>(null);

  const closeProfileDialog = useCallback(() => setProfileDialog(null), []);
  const closeGlossaryDialog = useCallback(() => setGlossaryDialog(null), []);

  return (
    <Box sx={{ mt: 3 }}>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Localization authoring</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reuse project-local language guidance and approved terminology across funnels, onboarding flows, ads, and other localized content.
      </Typography>

      {authoring.loading ? (
        <Box
          role="status"
          aria-label="Loading localization resources"
          data-testid={LOCALE_CONFIG.authoring.loading}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Loading profiles and glossaries…</Typography>
        </Box>
      ) : authoring.error ? (
        <Alert
          severity="error"
          data-testid={LOCALE_CONFIG.authoring.error}
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => void authoring.reload()}
              data-testid={LOCALE_CONFIG.authoring.retry}
            >
              Retry
            </Button>
          )}
        >
          {authoring.error.message}
        </Alert>
      ) : (
        <Stack spacing={3}>
          <ResourceSection
            title="Locale profiles"
            description="Tone, market direction, approved claims, and protected brand tokens."
            addLabel="Add profile"
            addTestId={LOCALE_CONFIG.authoring.profiles.add}
            emptyTestId={LOCALE_CONFIG.authoring.profiles.empty}
            emptyText="No active locale profiles yet. Add one to keep language guidance consistent."
            onAdd={() => setProfileDialog({ mode: 'create' })}
            saving={authoring.saving}
          >
            {authoring.profiles.map((profile) => (
              <ResourceRow
                key={profile.id}
                id={profile.id}
                sourceLocale={profile.sourceLocale}
                targetLocales={profile.targetLocales}
                detail={profile.context.tone ?? `Version ${profile.version}`}
                rowTestId={LOCALE_CONFIG.authoring.profiles.row(profile.id)}
                editTestId={LOCALE_CONFIG.authoring.profiles.edit(profile.id)}
                archiveTestId={LOCALE_CONFIG.authoring.profiles.archive(profile.id)}
                onEdit={() => setProfileDialog({ mode: 'edit', resource: profile })}
                onArchive={() => void authoring.archiveProfile(profile).catch(() => {})}
                saving={authoring.saving}
              />
            ))}
          </ResourceSection>

          <ResourceSection
            title="Glossaries"
            description="Approved source-to-target terminology shared by compatible locale profiles."
            addLabel="Add glossary"
            addTestId={LOCALE_CONFIG.authoring.glossaries.add}
            emptyTestId={LOCALE_CONFIG.authoring.glossaries.empty}
            emptyText="No active glossaries yet. Add one when terminology must stay exact."
            onAdd={() => setGlossaryDialog({ mode: 'create' })}
            saving={authoring.saving}
          >
            {authoring.glossaries.map((glossary) => (
              <ResourceRow
                key={glossary.id}
                id={glossary.id}
                sourceLocale={glossary.sourceLocale}
                targetLocales={glossary.targetLocales}
                detail={`${Object.keys(glossary.entries).length} terms · Version ${glossary.version}`}
                rowTestId={LOCALE_CONFIG.authoring.glossaries.row(glossary.id)}
                editTestId={LOCALE_CONFIG.authoring.glossaries.edit(glossary.id)}
                archiveTestId={LOCALE_CONFIG.authoring.glossaries.archive(glossary.id)}
                onEdit={() => setGlossaryDialog({ mode: 'edit', resource: glossary })}
                onArchive={() => void authoring.archiveGlossary(glossary).catch(() => {})}
                saving={authoring.saving}
              />
            ))}
          </ResourceSection>
        </Stack>
      )}

      {profileDialog && (
        <ProfileEditorDialog
          state={profileDialog}
          glossaries={authoring.glossaries}
          saving={authoring.saving}
          onClose={closeProfileDialog}
          onSave={async (draft) => {
            if (profileDialog.mode === 'create') await authoring.createProfile(draft);
            else {
              const { id: _immutableId, ...patch } = draft;
              await authoring.updateProfile(profileDialog.resource, {
                ...patch,
                glossaryRef: draft.glossaryRef ?? null,
              });
            }
            closeProfileDialog();
          }}
        />
      )}
      {glossaryDialog && (
        <GlossaryEditorDialog
          state={glossaryDialog}
          saving={authoring.saving}
          onClose={closeGlossaryDialog}
          onSave={async (draft) => {
            if (glossaryDialog.mode === 'create') await authoring.createGlossary(draft);
            else {
              const { id: _immutableId, ...patch } = draft;
              await authoring.updateGlossary(glossaryDialog.resource, patch);
            }
            closeGlossaryDialog();
          }}
        />
      )}
    </Box>
  );
};

interface ResourceSectionProps {
  title: string;
  description: string;
  addLabel: string;
  addTestId: string;
  emptyTestId: string;
  emptyText: string;
  onAdd: () => void;
  saving: boolean;
  children: React.ReactNode[];
}

function ResourceSection(props: ResourceSectionProps): JSX.Element {
  const children = React.Children.toArray(props.children);
  return (
    <Box component="section" aria-label={props.title}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box>
          <Typography variant="subtitle2">{props.title}</Typography>
          <Typography variant="body2" color="text.secondary">{props.description}</Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={props.onAdd}
          disabled={props.saving}
          data-testid={props.addTestId}
        >
          {props.addLabel}
        </Button>
      </Box>
      {children.length ? <List dense disablePadding>{children}</List> : (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid={props.emptyTestId}
          sx={{ py: 1.5, fontStyle: 'italic' }}
        >
          {props.emptyText}
        </Typography>
      )}
    </Box>
  );
}

interface ResourceRowProps {
  id: string;
  sourceLocale: string;
  targetLocales: string[];
  detail: string;
  rowTestId: string;
  editTestId: string;
  archiveTestId: string;
  onEdit: () => void;
  onArchive: () => void;
  saving: boolean;
}

function ResourceRow(props: ResourceRowProps): JSX.Element {
  return (
    <ListItem
      data-testid={props.rowTestId}
      divider
      secondaryAction={(
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label={`Edit ${props.id}`}
            data-testid={props.editTestId}
            onClick={props.onEdit}
            disabled={props.saving}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Archive ${props.id}`}
            data-testid={props.archiveTestId}
            onClick={props.onArchive}
            disabled={props.saving}
          >
            <ArchiveIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
    >
      <ListItemText
        sx={{ minWidth: 0, pr: 10 }}
        primary={(
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{props.id}</Typography>
            <Chip size="small" variant="outlined" label={props.sourceLocale} />
            <Typography variant="caption" color="text.secondary">→</Typography>
            {props.targetLocales.map((locale) => <Chip key={locale} size="small" label={locale} />)}
          </Stack>
        )}
        secondary={props.detail}
      />
    </ListItem>
  );
}

interface ProfileEditorDialogProps {
  state: ProfileDialogState;
  glossaries: ProjectLocaleGlossary[];
  saving: boolean;
  onClose: () => void;
  onSave: (draft: {
    id: string;
    sourceLocale: string;
    targetLocales: string[];
    context: ProjectLocaleProfileContext;
    glossaryRef?: string;
  }) => Promise<void>;
}

function ProfileEditorDialog(props: ProfileEditorDialogProps): JSX.Element {
  const existing = props.state.resource;
  const [id, setId] = useState(existing?.id ?? '');
  const [sourceLocale, setSourceLocale] = useState(existing?.sourceLocale ?? '');
  const [targetLocales, setTargetLocales] = useState(existing?.targetLocales.join(', ') ?? '');
  const [targetMarket, setTargetMarket] = useState(existing?.context.targetMarket ?? '');
  const [tone, setTone] = useState(existing?.context.tone ?? '');
  const [marketDirection, setMarketDirection] = useState(existing?.context.marketDirection ?? '');
  const [claims, setClaims] = useState(existing?.context.claims?.join('\n') ?? '');
  const [protectedTokens, setProtectedTokens] = useState(existing?.context.protectedTokens?.join('\n') ?? '');
  const [glossaryRef, setGlossaryRef] = useState(existing?.glossaryRef ?? '');
  const [error, setError] = useState<string | null>(null);
  const glossaryIds = useMemo(() => props.glossaries.map((entry) => entry.id).join(', '), [props.glossaries]);

  const save = async () => {
    setError(null);
    try {
      await props.onSave({
        id: id.trim(),
        sourceLocale: sourceLocale.trim(),
        targetLocales: commaSeparated(targetLocales),
        context: {
          ...(existing?.context ?? {}),
          targetMarket: optionalValue(targetMarket),
          tone: optionalValue(tone),
          marketDirection: optionalValue(marketDirection),
          claims: lineSeparated(claims),
          protectedTokens: lineSeparated(protectedTokens),
        },
        glossaryRef: optionalValue(glossaryRef),
      });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <SDialog
      open
      onClose={props.onClose}
      title={props.state.mode === 'create' ? 'Add locale profile' : `Edit ${existing?.id}`}
      maxWidth="sm"
      fullWidth
      data-testid={LOCALE_CONFIG.authoring.profileDialog}
      primaryAction={{
        label: props.saving ? 'Saving…' : 'Save profile',
        onClick: () => void save(),
        loading: props.saving,
        disabled: !id.trim() || !sourceLocale.trim() || commaSeparated(targetLocales).length === 0,
      }}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <StyledTextField label="Profile ID" value={id} onChange={setId} disabled={props.state.mode === 'edit'} fullWidth required />
        <StyledTextField label="Source locale" value={sourceLocale} onChange={setSourceLocale} fullWidth required />
        <StyledTextField label="Target locales" value={targetLocales} onChange={setTargetLocales} helperText="Comma-separated project locale codes" fullWidth required />
        <StyledTextField label="Target market" value={targetMarket} onChange={setTargetMarket} fullWidth />
        <StyledTextField label="Tone" value={tone} onChange={setTone} fullWidth />
        <StyledTextField label="Market direction" value={marketDirection} onChange={setMarketDirection} multiline rows={2} fullWidth />
        <StyledTextField label="Approved claims" value={claims} onChange={setClaims} multiline rows={3} helperText="One claim per line" fullWidth />
        <StyledTextField label="Protected tokens" value={protectedTokens} onChange={setProtectedTokens} multiline rows={3} helperText="One exact token per line" fullWidth />
        <StyledTextField
          label="Glossary ID"
          value={glossaryRef}
          onChange={setGlossaryRef}
          helperText={glossaryIds ? `Available: ${glossaryIds}` : 'Optional; add a compatible glossary first'}
          fullWidth
        />
      </Stack>
    </SDialog>
  );
}

interface GlossaryEditorDialogProps {
  state: GlossaryDialogState;
  saving: boolean;
  onClose: () => void;
  onSave: (draft: {
    id: string;
    sourceLocale: string;
    targetLocales: string[];
    entries: Record<string, string>;
  }) => Promise<void>;
}

function GlossaryEditorDialog(props: GlossaryEditorDialogProps): JSX.Element {
  const existing = props.state.resource;
  const [id, setId] = useState(existing?.id ?? '');
  const [sourceLocale, setSourceLocale] = useState(existing?.sourceLocale ?? '');
  const [targetLocales, setTargetLocales] = useState(existing?.targetLocales.join(', ') ?? '');
  const [entries, setEntries] = useState(existing ? glossaryText(existing.entries) : '');
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setError(null);
    try {
      await props.onSave({
        id: id.trim(),
        sourceLocale: sourceLocale.trim(),
        targetLocales: commaSeparated(targetLocales),
        entries: parseGlossaryText(entries),
      });
    } catch (cause) {
      setError(errorMessage(cause));
    }
  };

  return (
    <SDialog
      open
      onClose={props.onClose}
      title={props.state.mode === 'create' ? 'Add glossary' : `Edit ${existing?.id}`}
      maxWidth="sm"
      fullWidth
      data-testid={LOCALE_CONFIG.authoring.glossaryDialog}
      primaryAction={{
        label: props.saving ? 'Saving…' : 'Save glossary',
        onClick: () => void save(),
        loading: props.saving,
        disabled: !id.trim() || !sourceLocale.trim() || commaSeparated(targetLocales).length === 0 || !entries.trim(),
      }}
    >
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <StyledTextField label="Glossary ID" value={id} onChange={setId} disabled={props.state.mode === 'edit'} fullWidth required />
        <StyledTextField label="Source locale" value={sourceLocale} onChange={setSourceLocale} fullWidth required />
        <StyledTextField label="Target locales" value={targetLocales} onChange={setTargetLocales} helperText="Comma-separated project locale codes" fullWidth required />
        <StyledTextField
          label="Terms"
          value={entries}
          onChange={setEntries}
          multiline
          rows={8}
          helperText="One source = approved translation per line"
          fullWidth
          required
        />
      </Stack>
    </SDialog>
  );
}

function commaSeparated(value: string): string[] {
  return value.split(',').map((entry) => entry.trim()).filter(Boolean);
}

function lineSeparated(value: string): string[] | undefined {
  const entries = value.split('\n').map((entry) => entry.trim()).filter(Boolean);
  return entries.length ? entries : undefined;
}

function optionalValue(value: string): string | undefined {
  return value.trim() || undefined;
}

function glossaryText(entries: Record<string, string>): string {
  return Object.entries(entries).map(([source, target]) => `${source} = ${target}`).join('\n');
}

export function parseGlossaryText(value: string): Record<string, string> {
  const entries: Array<[string, string]> = [];
  const sources = new Map<string, { source: string; target: string }>();
  value.split('\n').forEach((line, index) => {
    if (!line.trim()) return;
    const separator = line.indexOf('=');
    if (separator < 1 || !line.slice(separator + 1).trim()) {
      throw new Error(`Line ${index + 1} must use “source = translation”.`);
    }
    const source = line.slice(0, separator).trim();
    const target = line.slice(separator + 1).trim();
    const sourceKey = source.normalize('NFKC').toLocaleLowerCase('en-US');
    const previous = sources.get(sourceKey);
    if (previous) {
      const duplicate = previous.target.normalize('NFKC').toLocaleLowerCase('en-US')
        === target.normalize('NFKC').toLocaleLowerCase('en-US');
      throw new Error(duplicate
        ? `Line ${index + 1} duplicates ${previous.source}.`
        : `Line ${index + 1} conflicts with ${previous.source}.`);
    }
    sources.set(sourceKey, { source, target });
    entries.push([source, target]);
  });
  if (!entries.length) throw new Error('Add at least one glossary term.');
  return Object.fromEntries(entries);
}

function errorMessage(value: unknown): string {
  return value instanceof Error ? value.message : 'Failed to save localization settings.';
}
